/**
 * Put a SAME-MATCH acca into the operator's own betslip, as a bet-builder item.
 *
 * ⛔⛔ THE STANDARD SLIP CANNOT HOLD ONE. Measured on parikara 2026-09-12: posting two
 * legs of one fixture with `toggleStake` reports `posted=2 landed=2` on our side and
 * the operator's slip keeps exactly ONE of them (owner's screenshot: Тотал Больше 2.5
 * 1.53 + Обе-Да 2.01 posted, coupon shows 1.53 alone). Same-event legs are refused —
 * which is the entire reason the book has a bet builder. So the acca must go in
 * through the bet-builder, or it does not go in at all.
 *
 * ⛔ AND THERE IS NO POSTMESSAGE FOR IT. The app's listener switch is
 * `EVENT_LISTENING_TYPES` = DESTROY 1 · THEME_CHANGE 2 · ROUTING_CHANGE 3 ·
 * OPEN_BETSLIP 4 · UPDATE_BONUS_INFO 5 · STICKY_MENU 6 · USER_CURRENCY 7 ·
 * CLOSE_BETSLIP 8 · BETSLIP_TOGGLE_STAKE 9 · TIME/ODD/LANGUAGE 13-15 · CASH_TYPE 16 ·
 * TOGGLE_PROJECT 17 (read out of the live bundle). Nothing for a bet builder, and
 * «Добавить в билет» makes no network call of its own — it is a local store action.
 * The only way in is the app's own module, which webpack leaves reachable.
 *
 * ⭐ THE CONTRACT, measured end to end on the live book (Наполи–Болонья 40343484):
 *
 * LEGACY SHAPE (2026-09-12):
 *
 *     setBetBuilderEventData({eventId, isLive, isSuperTip:false})
 *     getBetBuilderData(eventId, isLive, stakeIds)      → betBuilderData.BBF = the price
 *     addBetBuilderStake({selectedStakes, selectedStakesKeys:{id:"Id", marketId:"marketId"}})
 *
 *   2 legs (П1 + Тотал>2.5)            → Status "Success", one item, Factor 3.05, 2 legs
 *   3 legs (+ Обе-Да)                  → Status "Success", one item, Factor 4.15, 3 legs
 *
 * ⛔ `marketId` IS THE EVENT ID, not the market. The thunk groups the selection into
 * `[{Key: +stake[keys.marketId], Value: [stake[keys.id]]}]` and posts that to
 * `/Betting/AddStakes`. Grouped by MARKET id the operator answers
 * `ERROR_STAKE_DOS_NOT_EXIST` (Code 1304, ErrorCode 40) with a 200 — measured, and it
 * is the only failure this path produced. Grouped by EVENT id it answers "Success".
 *
 * REDUX-TOOLKIT SHAPE (2026-10-02): factories name
 * `betBuilder/setBetBuilderEventData`, `eventDetails/getBetBuilderData`, and
 * `eventDetails/onAddBetBuilderStake`. Their exports are minified and change per
 * build, so they are selected by action `type` / thunk `typePrefix`, never export
 * name. The thunks take `{stakeIds,eventId,isLive}` and
 * `{selectedStakes,selectedStakesKeys}` respectively. The betslip store likewise
 * has a minified export name; its state has both `betBuilder` and `common`.
 *
 * ⚠️ WE ARE REACHING INTO THE OPERATOR'S OWN MODULE. Everything here fails CLOSED and
 * nothing is resolved by webpack module id — those are build hashes and change on
 * every release. The service is found by the SOURCE SIGNATURE of its factory.
 */

/** The subset of the operator's betslip service this needs. */
interface BetBuilderService {
  setBetBuilderEventData: (a: { eventId: number; isLive: boolean; isSuperTip: boolean }) => void;
  getBetBuilderData: (eventId: number, isLive: boolean, stakeIds: number[]) => unknown;
  addBetBuilderStake: (a: {
    selectedStakes: Array<Record<string, unknown>>;
    selectedStakesKeys: { id: string; marketId: string };
    cardId?: unknown;
  }) => void;
  removeAllStakes?: () => void;
}

type WebpackRequire = ((id: string) => unknown) & { m?: Record<string, unknown> };

// Cached per page object, not globally: a test drives several fake pages, and a
// single global cache would hand the second one the first one's service.
type ServiceCache = BetBuilderService | { factoryCount: number };
const cache = new WeakMap<object, ServiceCache>();

/** What the service module's factory source must DEFINE for this to be it. */
const DEFINES = [
  /addBetBuilderStake\s*[=:]/,
  /setBetBuilderEventData\s*[=:]/,
  /getBetBuilderData\s*[=:]/,
];

// One borrowed require sees webpack's live factory table, including later chunks.
// Pushing another empty chunk on every retry needlessly mutates the operator page.
// ⛔ THE HOLDER IS CACHED, NOT THE ANSWER. Our script can run before the operator's
// webpack runtime: the registry is then a plain array, `push` calls nobody, and the
// runtime replays the queued chunk when it loads — so the callback fills the holder
// LATER. Caching "no require" at that moment would be a miss that never heals.
// ⛔ A page with no registry at all is not cached either: it may simply not exist yet.
const requireCache = new WeakMap<object, { req: WebpackRequire | null }>();

/** Borrow the app's own `__webpack_require__` by pushing an empty chunk once. */
function webpackRequire(win: Window & { webpackChunksportdigi_mobile?: unknown[] }): WebpackRequire | null {
  const held = requireCache.get(win);
  if (held) return held.req;
  try {
    const chunks = win.webpackChunksportdigi_mobile;
    if (!Array.isArray(chunks)) return null;
    const holder: { req: WebpackRequire | null } = { req: null };
    requireCache.set(win, holder);
    chunks.push([["bc-companion-" + Math.random()], {}, (r: WebpackRequire) => { holder.req = r; }]);
    return holder.req;
  } catch {
    return null;
  }
}

type BetslipStore = { getState: () => unknown; dispatch?: (action: unknown) => unknown };

function safelyDispatch(store: BetslipStore, action: unknown): unknown {
  try {
    const result = store.dispatch?.(action);
    // RTK dispatch can reject for a logged-out visitor (401). Consume that rejection
    // because these public service methods intentionally remain fire-and-forget.
    if (result && typeof (result as Promise<unknown>).then === "function") {
      void Promise.resolve(result).catch(() => undefined);
    }
    return result;
  } catch {
    return undefined;
  }
}

/**
 * The operator's betslip service, found by what its factory SOURCE defines.
 *
 * ⛔ Never by module id. `12395` is this build's hash for it; the next release
 * renumbers everything and an id lookup would silently return someone else's module.
 */
export function betBuilderService(w?: unknown): BetBuilderService | null {
  const win = (w ?? (typeof window !== "undefined" ? window : null)) as
    (Window & { webpackChunksportdigi_mobile?: unknown[] }) | null;
  if (!win) return null;
  try {
    const req = webpackRequire(win);
    const factories = req?.m;
    if (!req || !factories) return null;
    const factoryCount = Object.keys(factories).length;
    const cached = cache.get(win);
    if (cached && "setBetBuilderEventData" in cached) return cached;
    if (cached?.factoryCount === factoryCount) return null;
    for (const id of Object.keys(factories)) {
      let src = "";
      try { src = Function.prototype.toString.call(factories[id] as () => void); } catch { continue; }
      // The service module DEFINES these three; the components only call them.
      // ⚠️ Whitespace-tolerant on purpose: the shipped bundle is minified
      // (`t.addBetBuilderStake=e=>`) but a debug build spaces the assignment out,
      // and an exact-string match would silently find nothing there.
      if (!DEFINES.every((re) => re.test(src))) continue;
      let mod: Record<string, unknown> | null = null;
      try { mod = req(id) as Record<string, unknown>; } catch { continue; }
      if (!mod) continue;
      let svc: BetBuilderService | null = null;
      try {
        if (typeof mod.addBetBuilderStake !== "function") continue;
        if (typeof mod.setBetBuilderEventData !== "function") continue;
        if (typeof mod.getBetBuilderData !== "function") continue;
        svc = mod as unknown as BetBuilderService;
      } catch { continue; }
      cache.set(win, svc);
      return svc;
    }
    // The operator loads later chunks after the initial page; only retry when its
    // live factory table has grown.
    cache.set(win, { factoryCount });

    // Redux Toolkit: only factories naming both thunk strings may be executed.
    for (const id of Object.keys(factories)) {
      let src = "";
      try { src = Function.prototype.toString.call(factories[id] as () => void); } catch { continue; }
      if (!src.includes("eventDetails/onAddBetBuilderStake") ||
          !src.includes("eventDetails/getBetBuilderData")) continue;
      let mod: Record<string, unknown> | null = null;
      try { mod = req(id) as Record<string, unknown>; } catch { continue; }
      if (!mod) continue;
      let values: unknown[] = [];
      try { values = Object.values(mod); } catch { continue; }
      let setAction: ((a: unknown) => unknown) | undefined;
      let getThunk: ((a: unknown) => unknown) | undefined;
      let addThunk: ((a: unknown) => unknown) | undefined;
      for (const value of values) {
        try {
          if (typeof value !== "function") continue;
          const fn = value as ((a: unknown) => unknown) & { type?: unknown; typePrefix?: unknown };
          if (fn.type === "betBuilder/setBetBuilderEventData") setAction = fn;
          if (fn.typePrefix === "eventDetails/getBetBuilderData") getThunk = fn;
          if (fn.typePrefix === "eventDetails/onAddBetBuilderStake") addThunk = fn;
        } catch { /* an unusual export getter must not break resolution */ }
      }
      if (!setAction || !getThunk || !addThunk) continue;
      const store = betslipStore(win);
      if (!store || typeof store.dispatch !== "function") continue;
      const svc: BetBuilderService = {
        setBetBuilderEventData: (a) => { safelyDispatch(store, setAction(a)); },
        getBetBuilderData: (eventId, isLive, stakeIds) =>
          safelyDispatch(store, getThunk({ stakeIds, eventId, isLive })),
        addBetBuilderStake: (a) => { safelyDispatch(store, addThunk(a)); },
      };
      cache.set(win, svc);
      return svc;
    }
  } catch {
    /* fail closed */
  }
  return null;
}

/** Is the bet-builder route available at all on this page? */
export function canAddBetBuilder(w?: unknown): boolean {
  return betBuilderService(w) != null;
}

/** The betslip app's OWN redux store — a different store from
 *  `sportdigi.mobile.store`, and the one the bet-builder slice lives in. */
type StoreCache = BetslipStore | { factoryCount: number };
const storeCache = new WeakMap<object, StoreCache>();

function betslipStore(w?: unknown): BetslipStore | null {
  const win = (w ?? (typeof window !== "undefined" ? window : null)) as
    (Window & { webpackChunksportdigi_mobile?: unknown[] }) | null;
  if (!win) return null;
  try {
    const req = webpackRequire(win);
    const factories = req?.m;
    if (!req || !factories) return null;
    const factoryCount = Object.keys(factories).length;
    const cached = storeCache.get(win);
    if (cached && "getState" in cached) return cached;
    if (cached?.factoryCount === factoryCount) return null;
    for (const id of Object.keys(factories)) {
      let src = "";
      try { src = Function.prototype.toString.call(factories[id] as () => void); } catch { continue; }
      if (!/rootReducer/.test(src) || !/listenerMiddleware/.test(src)) continue;
      let mod: { store?: BetslipStore } | null = null;
      try { mod = req(id) as { store?: BetslipStore }; } catch { continue; }
      let st: BetslipStore | undefined;
      try { st = mod?.store; } catch { continue; }
      if (!st || typeof st.getState !== "function") continue;
      let state: { betBuilder?: unknown } | null = null;
      try { state = st.getState() as { betBuilder?: unknown }; } catch { continue; }
      if (!state || typeof state !== "object" || !("betBuilder" in state)) continue;
      storeCache.set(win, st);
      return st;
    }
    // Measured live: 15 of 645 factories mention `betBuilder`; exactly one declares
    // it as the reducer key, so this prefilter avoids executing the other fourteen.
    for (const id of Object.keys(factories)) {
      let src = "";
      try { src = Function.prototype.toString.call(factories[id] as () => void); } catch { continue; }
      if (!/betBuilder\s*:/.test(src)) continue;
      let mod: Record<string, unknown> | null = null;
      try { mod = req(id) as Record<string, unknown>; } catch { continue; }
      if (!mod) continue;
      let values: unknown[] = [];
      try { values = Object.values(mod); } catch { continue; }
      for (const value of values) {
        try {
          const st = value as BetslipStore;
          if (!st || typeof st.getState !== "function" || typeof st.dispatch !== "function") continue;
          const state = st.getState() as { betBuilder?: unknown; common?: unknown };
          if (!state || typeof state !== "object" || !("betBuilder" in state) || !("common" in state)) continue;
          storeCache.set(win, st);
          return st;
        } catch { /* export access and state reads fail closed */ }
      }
    }
    storeCache.set(win, { factoryCount });
  } catch {
    /* fail closed */
  }
  return null;
}

/** Where a card's price came from, and whether it can be trusted as ITS OWN. */
export interface PriceTrace {
  source: "service" | "replay" | "none";
  /** What the app's store held BEFORE we asked. */
  before: number | null;
  /** The store already held this exact BBF before we asked, so the value may
   *  belong to a DIFFERENT call — including another card's refusal (`0`) or
   *  another selection's price. `null` when nothing was read. */
  unchanged: boolean | null;
}

/**
 * ⭐ THE OPERATOR'S PRICE FOR A SELECTION, FETCHED BY THE OPERATOR ITSELF.
 *
 * ⛔ WHY NOT OUR OWN REPLAY. Replaying `GetBetBuilderStakesByEvent` with the app's JWT
 * needs that JWT to be readable, and it is not always: measured on a real tap
 * 2026-09-12, the strip read `★ NO (auth jwt)` and the call came back **401**, so the
 * card kept its multiplied product (3.34 where the book pays far less) and — worse —
 * the value check had no number to refuse on, so a pair the operator prices at 1.73
 * went into the slip below its own 1.93 leg. Pricing that fails silently is worse than
 * not pricing at all.
 *
 * `getBetBuilderData` goes out through the app's OWN http layer, which is authenticated
 * by construction — there is no token for us to be missing. The answer lands in the
 * betslip store's `betBuilder.betBuilderData.BBF`.
 *
 * Resolves the factor, or null when we could not read one (never a 0 we invented).
 */
export interface PriceTrace {
  source: "service" | "replay" | "none";
  /** What the app's store held BEFORE we asked. */
  before: number | null;
  /** The store already held this exact BBF before we asked, so the value may
   *  belong to a DIFFERENT call — including another card's refusal (`0`) or
   *  another selection's price. `null` when nothing was read. */
  unchanged: boolean | null;
}

/** The app's own user id from its global store: 0 when logged out, null when it
 *  cannot be read. */
function appUserId(w?: unknown): number | null {
  try {
    const win = (w ?? (typeof window !== "undefined" ? window : null)) as {
      sportdigi?: { mobile?: { store?: { getState?: () => unknown } } };
    } | null;
    const id = (win?.sportdigi?.mobile?.store?.getState?.() as { user?: { Id?: unknown } } | undefined)?.user?.Id;
    return typeof id === "number" && Number.isFinite(id) ? id : null;
  } catch {
    return null;
  }
}

// ⛔ ONE CALL AT A TIME. The store slot below is unkeyed, so two calls in flight
// (the pre-match drain + a live render, deck page or tap) read each other's BBF
// with `unchanged === false`, and that factor would be published as a shared
// `bb_verdict`. Every call waits for the previous one; a failure never breaks
// the chain. Each call's 5s timeout starts when its own turn starts.
let priceChain: Promise<unknown> = Promise.resolve();

export function priceViaService(
  eventId: number, isLive: boolean, stakeIds: number[], w?: unknown,
  trace?: PriceTrace,
): Promise<number | null> {
  const run = priceChain.then(() => priceOnce(eventId, isLive, stakeIds, w, trace));
  priceChain = run.catch(() => null);
  return run;
}

async function priceOnce(
  eventId: number, isLive: boolean, stakeIds: number[], w?: unknown,
  trace?: PriceTrace,
): Promise<number | null> {
  const svc = betBuilderService(w);
  const store = betslipStore(w);
  if (!svc || !store) return null;
  const ids = stakeIds.filter((n) => Number.isFinite(n) && n > 0);
  if (ids.length < 2) return null;
  // ⛔ NEVER WHILE LOGGED OUT. MEASURED on the live bundle 2026-10-02: the operator
  // rejects the price call for an anonymous visitor and its slice has no `rejected`
  // case, so `betBuilderLoading` stays TRUE in the operator's own store — a mark we
  // would leave on its bet builder, and a full PRICE_TIMEOUT_MS of polling per combo
  // here. Only an id that READS as 0 counts: an unreadable one (a page mid-hydration,
  // an older bundle) asks as before.
  if (appUserId(w) === 0) return null;
  const read = (): { bbf: number | null; loading: boolean } => {
    try {
      const bb = (store.getState() as { betBuilder?: {
        betBuilderData?: { BBF?: unknown }; betBuilderLoading?: unknown } }).betBuilder;
      const raw = bb?.betBuilderData?.BBF;
      return { bbf: typeof raw === "number" ? raw : null, loading: bb?.betBuilderLoading === true };
    } catch {
      return { bbf: null, loading: false };
    }
  };
  //  THE STORE IS ONE SLOT, SHARED BY EVERY CALL. `betBuilderData.BBF` is not
  // keyed by (eventId, stakeIds), so the read below cannot prove the value is
  // OURS: a leftover from the previous card — or from an empty selection, which
  // is `0` — lands on this card as its price. Snapshot before asking so the
  // drop telemetry can separate "the book said 0" from "we read a 0 that was
  // already there". MEASURED 2026-09-14: 829 refused cards sit on 161 fixtures
  // where the SAME pair shapes price fine elsewhere (П1 + Тотал>2.5 → 3.05), and
  // nothing in the payload said which of the two price sources produced the 0.
  const pre = read();
  try {
    svc.setBetBuilderEventData({ eventId, isLive, isSuperTip: false });
    svc.getBetBuilderData(eventId, isLive, ids);
  } catch {
    return null;
  }
  const deadline = Date.now() + PRICE_TIMEOUT_MS;
  while (Date.now() < deadline) {
    await new Promise((r) => setTimeout(r, 200));
    const { bbf, loading } = read();
    if (loading) continue;
    if (bbf != null) {
      if (trace) {
        trace.source = "service";
        trace.before = pre.bbf;
        trace.unchanged = pre.bbf === bbf;
      }
      return bbf;
    }
  }
  return null;
}

/** A `0` read from a store slot that already held `0` may belong to another
 *  card or to an empty selection — `betBuilderData.BBF` is unkeyed. Treat it as
 *  "could not tell" so the caller falls through to the replay. */
export function trustedFactor(factor: number | null, trace: PriceTrace): number | null {
  if (factor === 0 && trace.source === "service" && trace.unchanged === true) return null;
  return factor;
}

const PRICE_TIMEOUT_MS = 5000;

/** The operator's coupon, read from the app store (it mirrors the betslip app's). */
function couponItems(w?: unknown): Array<Record<string, unknown>> {
  try {
    const win = (w ?? (typeof window !== "undefined" ? window : null)) as {
      sportdigi?: { mobile?: { store?: { getState?: () => unknown } } };
    } | null;
    const s = win?.sportdigi?.mobile?.store?.getState?.() as {
      common?: { betslip?: { CheckedStakesList?: unknown } };
    } | undefined;
    const list = s?.common?.betslip?.CheckedStakesList;
    return Array.isArray(list) ? (list as Array<Record<string, unknown>>) : [];
  } catch {
    return [];
  }
}

/** How many bet-builder legs the coupon holds for this fixture right now. */
export function couponBetBuilderLegs(eventId: number, w?: unknown): number {
  let n = 0;
  for (const item of couponItems(w)) {
    const legs = item.BetBuilderStakes;
    if (!Array.isArray(legs) || !legs.length) continue;
    const first = legs[0] as { EventId?: unknown };
    if (Number(first?.EventId) !== eventId) continue;
    n = Math.max(n, legs.length);
  }
  return n;
}

export interface BetBuilderAddResult {
  /** The legs actually in the coupon afterwards — 0 means nothing landed. */
  landed: number;
  /** The operator's combined price for the selection (`BBF`), null when unknown. */
  factor: number | null;
  /** Why it did not land, when it did not. */
  reason: "ok" | "no-service" | "no-legs" | "not-landed";
}

const SETTLE_MS = 900;
const LANDED_TIMEOUT_MS = 6000;

/**
 * Add a same-match acca to the operator's coupon as a bet-builder item.
 *
 * Resolves once the coupon actually holds the legs, or with `landed: 0` — never on
 * the strength of having dispatched. The operator answers `Success` or
 * `ERROR_STAKE_DOS_NOT_EXIST` with a 200 either way, so only the coupon is proof.
 */
export async function addBetBuilderToCoupon(
  eventId: number, isLive: boolean, stakeIds: number[], w?: unknown,
): Promise<BetBuilderAddResult> {
  const svc = betBuilderService(w);
  if (!svc) return { landed: 0, factor: null, reason: "no-service" };
  const ids = stakeIds.filter((n) => Number.isFinite(n) && n > 0);
  if (ids.length < 2) return { landed: 0, factor: null, reason: "no-legs" };
  const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));
  try {
    svc.setBetBuilderEventData({ eventId, isLive, isSuperTip: false });
    // Loads the board AND the price for this exact selection. The app does the same
    // on every tap, so this is its own traffic pattern, not extra load.
    svc.getBetBuilderData(eventId, isLive, ids);
    await sleep(SETTLE_MS);
    svc.addBetBuilderStake({
      // ⛔ `marketId` carries the EVENT id — see the header note.
      selectedStakes: ids.map((id) => ({ Id: id, marketId: eventId })),
      selectedStakesKeys: { id: "Id", marketId: "marketId" },
    });
  } catch {
    return { landed: 0, factor: null, reason: "not-landed" };
  }
  const deadline = Date.now() + LANDED_TIMEOUT_MS;
  let landed = 0;
  while (Date.now() < deadline) {
    await sleep(300);
    landed = couponBetBuilderLegs(eventId, w);
    if (landed >= ids.length) break;
  }
  return {
    landed,
    factor: null,
    reason: landed >= ids.length ? "ok" : "not-landed",
  };
}
